import { el, setClass } from './dom';
import {
  clampLensZoom,
  focalMmForZoom,
} from '@/rendering/CameraManager';

/**
 * The lens, as the camera in everyone's pocket presents it.
 *
 * A field of view in degrees is a number for a renderer, not for a designer. What a designer knows
 * is that `0.5×` takes in the whole garden from the terrace and bows the beds outward, that `1×` is
 * what the eye does, and that `2×` stands back and flattens the planting into layers. So the control
 * is the row of pills off a phone: three stops, the one in force lit and carrying the exact figure,
 * everything else a tap away.
 *
 * Three behaviours, in the order they are reached for:
 *
 * - **Tap a stop.** Instant, and the middle one is the way home — which is why the default is a
 *   visible, permanently present target rather than a reset buried in a menu.
 * - **Drag across the row.** Continuous between the stops, on a log scale so a pixel of travel is
 *   the same proportion of zoom at either end. The lit pill keeps showing the true figure (`1.4×`),
 *   so a fine adjustment is never a lie about where the lens is.
 * - **Wheel or arrow keys.** One stop of the grid at a time, for the last few percent.
 *
 * It owns no camera. Everything it knows how to do is handed to `onChange`, which lets the same
 * widget drive the live view here and be tested as arithmetic elsewhere.
 */

/** The stops the row offers. Deliberately the phone's, not a lens catalogue's. */
export const LENS_PRESETS: readonly number[] = [0.5, 1, 2];
export const DEFAULT_LENS_ZOOM = 1;
/** Fine adjustment lands on this grid — 5% steps, fine enough to compose with, coarse enough to hit. */
export const LENS_STEP = 0.05;
/** Within this much of a stop, a fine adjustment is taken to have meant the stop. */
export const LENS_MAGNET = 0.04;
/** Pixels of horizontal drag that double the zoom. */
export const LENS_DRAG_PX_PER_DOUBLING = 150;
/** Below this much travel, a press was a click on a stop rather than a drag. */
const DRAG_THRESHOLD_PX = 4;


export interface LensControlDeps {
  /** Put the live camera on this lens. Called on every change, including mid-drag. */
  onChange: (zoom: number) => void;
  /**
   * The lens the camera is on now, read by {@link LensControl.sync}.
   *
   * Needed because the camera has another way of moving: restoring a saved shot puts the view back
   * on the lens it was taken with, and the row has to show that rather than what it last set.
   */
  current?: () => number;
}

/**
 * Round a fine adjustment onto the step grid, and onto a stop if it is all but there.
 *
 * The magnet matters more than it looks: without it, dragging back toward `1×` reliably stops at
 * `0.95×` or `1.05×`, and the view never quite returns to the one every other frame was composed on.
 */
export function snapLensZoom(zoom: number): number {
  const clamped = clampLensZoom(zoom);
  const nearestPreset = LENS_PRESETS.reduce((best, preset) =>
    Math.abs(preset - clamped) < Math.abs(best - clamped) ? preset : best,
  );
  if (Math.abs(nearestPreset - clamped) <= LENS_MAGNET) return nearestPreset;
  return clampLensZoom(Math.round(clamped / LENS_STEP) * LENS_STEP);
}

/**
 * The stop a zoom belongs to, measured proportionally rather than by subtraction.
 *
 * `1.4×` is a hair nearer `1` than `2` on a log scale and a long way nearer `1` to the eye, which is
 * the judgement the row has to make when it decides which pill lights up.
 */
export function nearestLensPreset(zoom: number): number {
  const clamped = clampLensZoom(zoom);
  return LENS_PRESETS.reduce((best, preset) =>
    Math.abs(Math.log(preset / clamped)) < Math.abs(Math.log(best / clamped)) ? preset : best,
  );
}

/** `0.5 → ".5"`, `1 → "1"`, `1.35 → "1.4"` — the phone's own shorthand, leading zero dropped. */
export function formatLensZoom(zoom: number, withSuffix = false): string {
  const rounded = Math.round(clampLensZoom(zoom) * 10) / 10;
  const text = (Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)).replace(
    /^0\./,
    '.',
  );
  return withSuffix ? `${text}×` : text;
}

/** How a stop describes itself, for its tooltip and its screen-reader label. */
export function describeLens(zoom: number): string {
  const mm = Math.round(focalMmForZoom(zoom));
  const character = zoom < 1 ? 'wide' : zoom > 1 ? 'telephoto' : 'standard';
  return `${formatLensZoom(zoom, true)} ${character} — ${mm} mm equivalent`;
}

/** Where a drag has taken the lens: `start` scaled by how far the pointer travelled. */
export function lensZoomAfterDrag(startZoom: number, deltaX: number): number {
  return snapLensZoom(startZoom * Math.pow(2, deltaX / LENS_DRAG_PX_PER_DOUBLING));
}

export class LensControl {
  /** The pill row. Mounted into the render bar's corner, immediately left of Render. */
  readonly root: HTMLElement;

  /**
   * Every session starts at `1x`.
   *
   * The lens used to be remembered across reloads, which sounds like a courtesy and reads as a
   * fault: `1x` is what the eye does, so it is the frame every other view is judged against, and
   * someone returning to a site they left at `0.5x` sees bowed beds and a garden that is not the
   * shape they built. A zoom is a thing you reach for in order to look at something, not a
   * property of the site -- and unlike the pan and orbit, which the saved document carries, this
   * was being restored from whichever site the browser happened to have open last.
   */
  private zoom = DEFAULT_LENS_ZOOM;
  private readonly pills = new Map<number, HTMLButtonElement>();
  /** Set while a press is being read as a drag, so the click it ends with is not also a stop. */
  private dragged = false;
  private drag: { pointerId: number; startX: number; startZoom: number } | null = null;

  constructor(private readonly deps: LensControlDeps) {
    for (const preset of LENS_PRESETS) {
      const pill = el('button', {
        class: 'lg-lens__pill',
        attrs: { type: 'button', 'aria-pressed': 'false' },
        text: formatLensZoom(preset),
        title: describeLens(preset),
        on: { click: () => this.handlePillClick(preset) },
      });
      this.pills.set(preset, pill);
    }

    this.root = el(
      'div',
      {
        class: 'lg-lens',
        attrs: { role: 'group', 'aria-label': 'Camera lens' },
        on: {
          pointerdown: (event) => this.handlePointerDown(event),
          pointermove: (event) => this.handlePointerMove(event),
          pointerup: (event) => this.handlePointerUp(event),
          pointercancel: (event) => this.handlePointerUp(event),
          keydown: (event) => this.handleKey(event),
        },
      },
      ...this.pills.values(),
    );

    // Non-passive on purpose: the wheel adjusts the lens here rather than scrolling anything, and a
    // page that scrolls under the pointer instead would be the widget failing to claim the gesture.
    this.root.addEventListener('wheel', this.onWheel, { passive: false });

    // The remembered lens is only a preference until it is on the camera, so it is pushed rather
    // than waited for. A first session with nothing stored pushes `1×`, which is already true.
    this.apply(this.zoom);
  }

  /** The lens in force. */
  get value(): number {
    return this.zoom;
  }

  /** Re-read the camera after something else moved it — restoring a shot, most of all. */
  sync(): void {
    const current = this.deps.current?.();
    if (current === undefined) return;
    const next = clampLensZoom(current);
    if (next === this.zoom) return;
    this.zoom = next;
    this.refresh();
  }

  private handlePillClick(preset: number): void {
    // The click that ends a drag lands on whichever pill the pointer stopped over, which is not a
    // choice the user made — it would undo the adjustment they just finished.
    if (this.dragged) {
      this.dragged = false;
      return;
    }
    this.apply(preset);
  }

  private handlePointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;
    this.dragged = false;
    this.drag = { pointerId: event.pointerId, startX: event.clientX, startZoom: this.zoom };
  }

  /**
   * The pointer is not captured until the press is known to be a drag.
   *
   * Capturing on press would retarget the click that follows it to this element, and the stops would
   * stop being clickable — the tap being the thing this control is reached for most.
   */
  private handlePointerMove(event: PointerEvent): void {
    const drag = this.drag;
    if (drag === null || drag.pointerId !== event.pointerId) return;
    const deltaX = event.clientX - drag.startX;
    if (!this.dragged) {
      if (Math.abs(deltaX) < DRAG_THRESHOLD_PX) return;
      this.dragged = true;
      this.root.setPointerCapture(event.pointerId);
    }
    this.apply(lensZoomAfterDrag(drag.startZoom, deltaX));
  }

  private handlePointerUp(event: PointerEvent): void {
    if (this.drag?.pointerId !== event.pointerId) return;
    this.drag = null;
    if (this.root.hasPointerCapture(event.pointerId)) this.root.releasePointerCapture(event.pointerId);
  }

  private readonly onWheel = (event: WheelEvent): void => {
    event.preventDefault();
    this.nudge(event.deltaY < 0 ? 1 : -1);
  };

  private handleKey(event: KeyboardEvent): void {
    const up = event.key === 'ArrowRight' || event.key === 'ArrowUp';
    const down = event.key === 'ArrowLeft' || event.key === 'ArrowDown';
    if (!up && !down) return;
    const direction = up ? 1 : -1;
    event.preventDefault();
    // Claimed here as well: the editor reads bare keys off `window`, and an arrow that both nudged
    // the lens and nudged the selection would be one gesture doing two jobs.
    event.stopPropagation();
    this.nudge(direction);
  }

  /** One step of the grid, ignoring the magnet so a stop can actually be stepped away from. */
  private nudge(direction: number): void {
    const next = clampLensZoom(
      Math.round((this.zoom + direction * LENS_STEP) / LENS_STEP) * LENS_STEP,
    );
    this.apply(next);
  }

  /**
   * Set the lens, draw it, and hand it to the camera.
   *
   * Unconditional rather than guarded on a change: a drag reaching the end of its range keeps
   * sending the same value, and the cost of re-applying it is one projection matrix.
   */
  private apply(zoom: number): void {
    this.zoom = clampLensZoom(zoom);
    this.refresh();
    this.deps.onChange(this.zoom);
  }

  private refresh(): void {
    const active = nearestLensPreset(this.zoom);
    for (const [preset, pill] of this.pills) {
      const isActive = preset === active;
      // The lit pill carries the *true* figure, so a dragged 1.4× never reads as a plain 1×.
      pill.textContent = isActive ? formatLensZoom(this.zoom, true) : formatLensZoom(preset);
      pill.setAttribute('aria-pressed', String(isActive));
      pill.setAttribute(
        'aria-label',
        isActive ? describeLens(this.zoom) : `Switch to ${describeLens(preset)}`,
      );
      pill.title = isActive && this.zoom !== active ? describeLens(this.zoom) : describeLens(preset);
      setClass(pill, 'is-active', isActive);
    }
    setClass(this.root, 'is-shifted', this.zoom !== DEFAULT_LENS_ZOOM);
  }

  dispose(): void {
    this.root.removeEventListener('wheel', this.onWheel);
    this.root.remove();
  }
}
